import { formatDateISO, getStateName, PolicyDetails } from "shared";
import styles from "./PolicyTiles.module.scss";
import { useDownloadIdCard } from "../../Insurance/useDownloadIdCard";
import { IdCard } from "../../../svg";
import { TagAction } from "../../../types/Tag.types";
import { useDialog } from "../../Dialog";
import { useEffect, useState } from "react";
import { CompOnlyDialog } from "../../Insurance/CompOnlyDialog";
import { DownloadDocumentErrorAlert } from "../../Insurance/DownloadDocumentErrorAlert";
import { getVehiclesLabel } from "../../../utils/vehicleUtils";
import { mt } from "../../../utils/tracking";
import { PopoverTile } from "../../PopoverTile";
import { validateState } from "@hagerty/self-service-components/api/utils";

type IdCardTileProps = {
  policy: PolicyDetails;
};

const idTagAction: TagAction = {
  actionName: "ID Card",
  actionType: "button",
};

export const IdCardTile = ({ policy }: IdCardTileProps) => {
  const { policyNumber, country } = policy;
  const vehicleNamesLabel = getVehiclesLabel(policy.vehicles);
  const futureVersion = policy.futureVersion;
  const hasFutureVersion = !!futureVersion;

  const [isErrorAlertOpen, setIsErrorAlertOpen] = useState(false);
  const compOnlyDialog = useDialog();

  const {
    download: downloadIdCard,
    isDownloading: isDownloadingIdCard,
    error: downloadIdCardError,
  } = useDownloadIdCard(policyNumber, country);

  const {
    download: downloadFutureIdCard,
    isDownloading: isDownloadingFutureIdCard,
    error: downloadFutureIdCardError,
  } = hasFutureVersion
    ? useDownloadIdCard(futureVersion.policyNumber, country)
    : { download: () => undefined, isDownloading: false, error: null };

  useEffect(() => {
    setIsErrorAlertOpen(!!(downloadIdCardError || downloadFutureIdCardError));
  }, [downloadIdCardError, downloadFutureIdCardError]);

  return (
    <>
      <PopoverTile
        icon={<img src={IdCard} alt="ID Card" aria-hidden="true" />}
        isLoading={isDownloadingIdCard || isDownloadingFutureIdCard}
        className={styles.tile}
        cta="ID Card"
        ariaLabel={`Access Insurance ID for your ${vehicleNamesLabel} on policy ${policyNumber}`}
        tagAction={idTagAction}
        onClick={() =>
          policy.hasIdCard ? downloadIdCard() : compOnlyDialog.open()
        }
        ctaList={
          hasFutureVersion && policy.hasIdCard
            ? [
                {
                  tagAction: idTagAction,
                  onClick: downloadIdCard,
                  label: `Current: ${formatDateISO(
                    policy.effectiveDate
                  )} - ${formatDateISO(policy.expirationDate)}`,
                },
                {
                  tagAction: idTagAction,
                  onClick: downloadFutureIdCard,
                  label: `Future: ${formatDateISO(
                    futureVersion.effectiveDate
                  )} - ${formatDateISO(futureVersion.expirationDate)}`,
                },
              ]
            : undefined
        }
      />

      {compOnlyDialog.isOpen && (
        <CompOnlyDialog
          policyName={getStateName(validateState(policy.stateRegion))}
          compOnlyType={policy.compOnlyType ?? "STANDARD"}
          onClose={() => {
            mt.trackPopupClose();
            compOnlyDialog.close();
          }}
        />
      )}

      {isErrorAlertOpen && (
        <DownloadDocumentErrorAlert
          documentName="ID Card"
          onClose={() => setIsErrorAlertOpen(false)}
        />
      )}
    </>
  );
};
